import Application from "../../Application";
import ChangePasswordInteractor from "../interactor/ChangePasswordInteractor";
import ShowSuccessMessageInteractor from "../../core/interactor/notifications/ShowSuccessMessageInteractor";
import withStore, {
  TLoadingAwarePresenter,
  TPresentable
} from "../../core/presenter/withStore";
import ValidatableObject from "../../core/model/validation/ValidatableObject";
import RequiredValueValidator from "../../core/model/validation/validators/RequiredValueValidator";
import PasswordMatchValidator from "../../core/model/validation/validators/PasswordMatchValidator";
import { TValidationResult } from "../../core/model/validation/ValidationResult";
import PasswordValidator from "../../core/model/validation/validators/PasswordValidator";
import ShowErrorMessageInteractor from "../../core/interactor/notifications/ShowErrorMessageInteractor";
import PasswordChangePresentationModel, {
  TPasswordChangePresentationModel
} from "./model/PasswordChangePresentationModel";
import LoginInteractor from "../interactor/LoginInteractor";

export interface TChangePasswordPresenter extends TLoadingAwarePresenter {
  password: TPasswordChangePresentationModel;
  submitButtonDisabled: boolean;
  validationPasswordErrors: any;
  username?: string;
  setPassword: boolean;
}
export interface IChangePasswordPresenter
  extends TChangePasswordPresenter,
    TPresentable {
  setPasswordValue(key: string, value: string);
  validatePassword(password);
  changePassword(): void;
  onClickBackButton(): void;
}

const defaultState: TChangePasswordPresenter = {
  password: {} as TPasswordChangePresentationModel,
  submitButtonDisabled: true,
  setPassword: false,
  validationPasswordErrors: {} as any
};

const ChangePasswordPresenter = withStore<
  IChangePasswordPresenter,
  TChangePasswordPresenter
>(({ application, store, loader, translate }): IChangePasswordPresenter => {
  const _store = store;
  const _application: Application = application;
  const state = _store.getState<TChangePasswordPresenter>();

  const changePassword = async () => {
    try {
      const { password, username, setPassword } =
        _store.getState<TChangePasswordPresenter>();
      const validationResult = await validatePassword(password);

      if (validationResult && validationResult.isValid) {
        await _application.container
          .resolve<ChangePasswordInteractor>("changePassword")
          .execute(password.newPassword, password.oldPassword, username);
        _application.container
          .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
          .execute(
            translate("settings.password.messages.change_password_success")
          );
        if (setPassword && username) {
          await _application.container
            .resolve<LoginInteractor>("login")
            .execute(username, password.newPassword);
        }
      }
    } catch (error: any) {
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute(error.message);
    }
  };
  const onClickBackButton = () => {
    const { setPassword } = _store.getState<TChangePasswordPresenter>();
    return setPassword
      ? _application.navigator.push({
          pathname: `/login`
        })
      : _application.navigator.push({
          pathname: `/`
        });
  };
  const setPasswordValue = (key: string, value: string) => {
    const password = _store.getState<TChangePasswordPresenter>().password;
    password[key] = value;
    _store.update({
      password
    });
    checkSubmitButtonDisabled();
    return validatePassword(password);
  };

  const checkSubmitButtonDisabled = () => {
    const validationErrors =
      _store.getState<TChangePasswordPresenter>().validationPasswordErrors;
    if (hasErrors(validationErrors)) {
      return _store.update({ submitButtonDisabled: true });
    }
    return _store.update({ submitButtonDisabled: false });
  };

  const hasErrors = (object) => {
    return Object.entries(object).length;
  };
  const validatePassword = async (password) => {
    const validatableObject = ValidatableObject(
      PasswordChangePresentationModel(password)
    );
    validatableObject.addValidator(RequiredValueValidator("newPassword"));
    validatableObject.addValidator(
      RequiredValueValidator("confirmNewPassword")
    );
    validatableObject.addValidator(
      PasswordMatchValidator("confirmNewPassword", "newPassword")
    );
    validatableObject.addValidator(PasswordValidator("newPassword"));
    const result: TValidationResult = await validatableObject.validate();
    _store.update({
      validationPasswordErrors: result.validationErrors,
      submitButtonDisabled: hasErrors(result.validationErrors) ? true : false
    });
    return result;
  };

  return {
    ...state,
    store: _store,
    loader,
    application: _application,
    translate,
    setPasswordValue,
    validatePassword,
    changePassword,
    onClickBackButton
  };
}, defaultState);

export default ChangePasswordPresenter;
